import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api, call } from './client';
import { qk } from './keys';
import type { Department, TaskPolicy, User } from './types';

/** Усі працівники (зокрема деактивовані — для імен в історії). */
export function useUsers() {
  return useQuery({
    queryKey: qk.users,
    queryFn: async () => (await call(api.GET('/api/v1/users', { params: { query: { includeSuspended: true } } }))).data,
    staleTime: 5 * 60_000,
  });
}

export function useUserMap(): Map<string, User> {
  const { data } = useUsers();
  return useMemo(() => new Map((data ?? []).map((u) => [u.id, u])), [data]);
}

export function useActiveUsers(): User[] {
  const { data } = useUsers();
  return useMemo(() => (data ?? []).filter((u) => u.status === 'active'), [data]);
}

export function useDepartments() {
  return useQuery({
    queryKey: qk.departments,
    queryFn: async () => (await call(api.GET('/api/v1/departments'))).data,
    staleTime: 10 * 60_000,
  });
}

export function useTeams() {
  return useQuery({
    queryKey: qk.teams,
    queryFn: async () => (await call(api.GET('/api/v1/teams'))).data,
    staleTime: 10 * 60_000,
  });
}

export function useBusinessRoles() {
  return useQuery({
    queryKey: qk.businessRoles,
    queryFn: async () => (await call(api.GET('/api/v1/business-roles'))).data,
    staleTime: Infinity,
  });
}

/** Політика доступу: які бізнес-ролі відкривають фінансові дані (читає адміністратор, змінює лише власник). */
export function useAccessPolicy(enabled = true) {
  return useQuery({
    queryKey: qk.accessPolicy,
    queryFn: async () => call(api.GET('/api/v1/organization/access-policy')),
    staleTime: 5 * 60_000,
    enabled,
  });
}

type CreateUserBody = { email: string; name: string; role?: User['role']; jobTitle?: string; departmentId?: string; managerId?: string };
export type PatchUserBody = {
  name?: string;
  role?: User['role'];
  status?: User['status'];
  jobTitle?: string | null;
  departmentId?: string | null;
  managerId?: string | null;
  deputyId?: string | null;
  businessRoleCodes?: string[];
};

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserBody) => call(api.POST('/api/v1/users', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.users }),
  });
}

export function usePatchUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchUserBody }) =>
      call(api.PATCH('/api/v1/users/{id}', { params: { path: { id } }, body })),
    onSuccess: (u) => {
      qc.setQueryData<User[]>(qk.users, (list) => list?.map((x) => (x.id === u.id ? u : x)));
      void qc.invalidateQueries({ queryKey: qk.users });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useHandover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, toUserId, reason }: { id: string; toUserId: string; reason: string }) =>
      call(api.POST('/api/v1/users/{id}/handover', { params: { path: { id } }, body: { toUserId, reason } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.tasksAll });
    },
  });
}

type DeptBody = {
  name: string;
  parentId?: string | null;
  kind?: Department['kind'];
  headUserId?: string | null;
  valuableFinalProduct?: string | null;
  position?: number;
};

export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: DeptBody) => call(api.POST('/api/v1/departments', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.departments }),
  });
}

export function usePatchDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<DeptBody> & { archived?: boolean } }) =>
      call(api.PATCH('/api/v1/departments/{id}', { params: { path: { id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.departments }),
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; departmentId?: string; memberIds?: string[] }) =>
      call(api.POST('/api/v1/teams', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.teams }),
  });
}

export function usePatchTaskPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<TaskPolicy>) => call(api.PATCH('/api/v1/organization/task-policy', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
}

export const ORG_ROLE_LABELS: Record<User['role'], string> = {
  owner: 'Власник',
  admin: 'Адміністратор',
  member: 'Працівник',
  guest: 'Гість',
};

export const DEPT_KIND_LABELS: Record<Department['kind'], string> = {
  division: 'Відділення',
  department: 'Відділ',
  section: 'Секція',
};
